import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { PMT2Product } from '../types/pmt2Types';
import { addDays, differenceInDays, getYear, min, max, setYear } from 'date-fns';
import { calculateDaysFarming, getFarmingHistoryCalcs2Data, FarmingHistoryCalcs2Document, FarmingHistoryCalcs2Entry } from './pmt2FarmingHistoryCalcs2Service';

// Collection names
const GPO_HISTORY_COLLECTION = 'pmt2_gpohistory';
const PRODUCT_COLLECTION = 'pmt2_data';

/**
 * Interface for a row in the GPO Performance table
 */
export interface GpoPerformanceRow {
  index: number;
  grossProfit: number;
  maxBid: number;
  startDate: Date;
  days: number;
  profitPerDay: number;
  endDate: Date;
  season1Days: string | null;
  season2Days: string | null;
  farmingStartDate: Date | null;
  farmingEndDate: Date | null;
  daysFarming: number | null;
}

/**
 * Helper function to parse a currency string to a number
 * @param value Currency string (e.g., "$14.00")
 * @returns Parsed number or null if parsing fails
 */
const parseCurrency = (value: string | null | undefined): number | null => {
  if (!value) return null;
  // Remove any currency symbols or other non-numeric characters except decimal point and minus sign
  const cleanValue = value.toString().replace(/[^0-9.-]/g, '');
  return cleanValue ? parseFloat(cleanValue) : null;
};

/**
 * Helper function to calculate the number of days that overlap between a date range and a season
 * @param startDate Start date of the range
 * @param endDate End date of the range
 * @param seasonStartDate Start date of the season
 * @param seasonEndDate End date of the season
 * @returns Object containing the number of overlapping days and the percentage of the season covered
 */
const calculateSeasonOverlap = (
  startDate: Date,
  endDate: Date,
  seasonStartDate: Date | null | undefined,
  seasonEndDate: Date | null | undefined
): { days: number; percentage: number } => {
  // If season dates are missing or invalid, return 0 days and 0%
  if (!seasonStartDate || !seasonEndDate || isNaN(seasonStartDate.getTime()) || isNaN(seasonEndDate.getTime())) {
    return { days: 0, percentage: 0 };
  }

  // Get the range start and end
  const rangeStart = startDate;
  const rangeEnd = endDate;

  // Get all years in the range
  const startYear = getYear(rangeStart);
  const endYear = getYear(rangeEnd);
  const years: number[] = [];
  for (let year = startYear; year <= endYear; year++) {
    years.push(year);
  }

  // Calculate overlap for each year and sum
  let totalOverlapDays = 0;
  for (const year of years) {
    // Create season dates for this year
    const yearSeasonStart = setYear(new Date(seasonStartDate), year);
    const yearSeasonEnd = setYear(new Date(seasonEndDate), year);

    // Calculate overlap
    const overlapStart = max([rangeStart, yearSeasonStart]);
    const overlapEnd = min([rangeEnd, yearSeasonEnd]);

    // Check if there's a valid overlap
    if (overlapStart <= overlapEnd) {
      // Add 1 to include both start and end dates (inclusive)
      const overlapDays = differenceInDays(overlapEnd, overlapStart) + 1;
      totalOverlapDays += overlapDays;
    }
  }

  // Calculate percentage of season covered
  const seasonDays = differenceInDays(seasonEndDate, seasonStartDate) + 1;
  const percentage = seasonDays > 0 ? (totalOverlapDays / seasonDays) * 100 : 0;

  return {
    days: totalOverlapDays,
    percentage: Math.round(percentage)
  };
};

/**
 * Format the season days result as "X (Y%)"
 * @param result Result from calculateSeasonOverlap
 * @returns Formatted string or null if days is 0
 */
const formatSeasonDays = (result: { days: number; percentage: number }): string | null => {
  if (result.days === 0) return null;
  return `${result.days} (${result.percentage}%)`;
};

/**
 * Helper function to safely parse a Firestore timestamp to a Date object
 * @param timestamp Firestore timestamp or serialized timestamp
 * @returns Date object or null if parsing fails
 */
const parseTimestamp = (timestamp: any): Date | null => {
  try {
    if (!timestamp) return null;
    
    if (timestamp.toDate) {
      // It's a Firestore Timestamp
      return timestamp.toDate();
    } else if (timestamp.seconds) {
      // It's a serialized Timestamp
      return new Date(timestamp.seconds * 1000);
    } else if (typeof timestamp === 'string') {
      // It's a date string
      return new Date(timestamp);
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Fetches and processes GPO Performance data for a specific ASIN
 * @param companyName Company name
 * @param asin ASIN
 * @returns Array of GpoPerformanceRow objects
 */
export const getGpoPerformanceData = async (
  companyName: string,
  asin: string
): Promise<GpoPerformanceRow[]> => {
  try {
    // Fetch GPO history data
    const gpoHistoryRef = doc(db, GPO_HISTORY_COLLECTION, companyName, 'entries', asin);
    const gpoHistoryDoc = await getDoc(gpoHistoryRef);

    if (!gpoHistoryDoc.exists()) {
      return [];
    }

    const gpoHistoryData = gpoHistoryDoc.data();

    // Fetch farming history data (once for all GPO periods)
    let farmingHistoryData: FarmingHistoryCalcs2Document | null = null;
    try {
      farmingHistoryData = await getFarmingHistoryCalcs2Data(companyName, asin);
    } catch (error) {
      // Continue with null value
    }

    // Filter and sort farming entries if available
    let farmingEntries: FarmingHistoryCalcs2Entry[] = [];
    if (farmingHistoryData && farmingHistoryData.entries && Array.isArray(farmingHistoryData.entries)) {
      // Filter entries to only include "Farming" status
      farmingEntries = farmingHistoryData.entries.filter(entry => 
        entry.clean_status === 'Farming' || entry.new_value === 'Farming'
      );

      // Sort by period_start date (newest first, then we'll reverse when creating rows)
      farmingEntries.sort((a, b) => {
        const dateA = parseTimestamp(a.period_start) || new Date(0);
        const dateB = parseTimestamp(b.period_start) || new Date(0);
        return dateB.getTime() - dateA.getTime();
      });
    }

    // Fetch product data for season dates
    const productRef = doc(db, PRODUCT_COLLECTION, companyName, 'products', asin);
    const productDoc = await getDoc(productRef);
    
    let productData: PMT2Product | null = null;
    if (productDoc.exists()) {
      productData = {
        id: productDoc.id,
        ...productDoc.data()
      } as PMT2Product;
    }

    // Parse season dates if available
    let season1StartDate: Date | null = null;
    let season1EndDate: Date | null = null;
    let season2StartDate: Date | null = null;
    let season2EndDate: Date | null = null;

    if (productData) {
      // Parse season dates (they are Firestore Timestamps)
      season1StartDate = parseTimestamp(productData['Season 1 - Start | On Date']);
      season1EndDate = parseTimestamp(productData['Season 1 - End | Off Date']);
      season2StartDate = parseTimestamp(productData['Season 2 - Start | On Date']);
      season2EndDate = parseTimestamp(productData['Season 2 - End | Off Date']);
    }

    // Extract all GPO data entries
    const gpoEntries: {
      index: number;
      grossProfit: number;
      maxBid: number;
      startDate: Date;
      days: number;
      profitPerDay: number;
      endDate: Date;
    }[] = [];

    // First pass: Extract all GPO data entries
    for (let i = 1; i <= 5; i++) {
      // Check if data exists for this index
      const gpoKey = `gpo_${i}`;
      const gpoDateKey = `gpo_date_${i}`;
      const gpoDaysKey = `gpo_days_${i}`;
      const profitPerDayKey = `profit_per_day_${i}`;

      // If any of the required fields is missing, stop processing
      if (!gpoHistoryData[gpoKey] || !gpoHistoryData[gpoDateKey] || 
          !gpoHistoryData[gpoDaysKey] || !gpoHistoryData[profitPerDayKey]) {
        break;
      }

      // Parse values
      const grossProfit = parseCurrency(gpoHistoryData[gpoKey]);
      if (grossProfit === null) continue;

      // Parse date (it's a Firestore Timestamp)
      const startDate = parseTimestamp(gpoHistoryData[gpoDateKey]);
      
      // Skip this row if we couldn't parse the date
      if (!startDate || isNaN(startDate.getTime())) {
        continue;
      }

      // Parse days (it's a number)
      const days = parseInt(gpoHistoryData[gpoDaysKey], 10);
      if (isNaN(days)) continue;

      // Parse profit per day (it's a string like "$20.14")
      const profitPerDay = parseCurrency(gpoHistoryData[profitPerDayKey]);
      if (profitPerDay === null) continue;

      // Calculate max bid (20% of gross profit)
      const maxBid = grossProfit * 0.2;

      // Calculate end date (start date + days, inclusive)
      // For the first row (index 1), add one extra day to the end date
      const endDate = i === 1 
        ? addDays(startDate, days) // For the first row, don't subtract 1
        : addDays(startDate, days - 1); // For other rows, subtract 1 because the start date is included

      // Add to GPO entries
      gpoEntries.push({
        index: i,
        grossProfit,
        maxBid,
        startDate,
        days,
        profitPerDay,
        endDate
      });
    }

    // Extract all farming entries with "Farming" status
    const farmingDateEntries: {
      start: Date | null;
      end: Date | null;
    }[] = [];

    if (farmingEntries.length > 0) {
      // Filter and process farming entries
      for (const entry of farmingEntries) {
        const farmingStartDate = parseTimestamp(entry.period_start);
        const farmingEndDate = parseTimestamp(entry.period_end);
        
        if (farmingStartDate) {
          farmingDateEntries.push({
            start: farmingStartDate,
            end: farmingEndDate
          });
        }
      }
    }

    // Sort the farming date entries to match the Google Sheets order (newest first)
    farmingDateEntries.sort((a, b) => {
      const dateA = a.start || new Date(0);
      const dateB = b.start || new Date(0);
      return dateB.getTime() - dateA.getTime();
    });

    // Determine how many rows to display (max of GPO entries and farming entries)
    const numRows = Math.max(gpoEntries.length, farmingDateEntries.length);
    
    // Create performance rows
    const performanceRows: GpoPerformanceRow[] = [];
    for (let i = 0; i < numRows; i++) {
      // Get GPO data (repeat the last entry if we have more farming entries than GPO entries)
      const gpoIndex = i < gpoEntries.length ? i : -1;
      const gpoData = gpoIndex >= 0 ? gpoEntries[gpoIndex] : null;
      
      // Get farming data (if available for this row)
      let farmingStartDate: Date | null = null;
      let farmingEndDate: Date | null = null;
      
      if (i < farmingDateEntries.length) {
        farmingStartDate = farmingDateEntries[i].start;
        farmingEndDate = farmingDateEntries[i].end;
      }

      // Create performance row
      let performanceRow: GpoPerformanceRow;
      
      if (gpoData) {
        // Calculate season days
        const season1Overlap = calculateSeasonOverlap(gpoData.startDate, gpoData.endDate, season1StartDate, season1EndDate);
        const season2Overlap = calculateSeasonOverlap(gpoData.startDate, gpoData.endDate, season2StartDate, season2EndDate);

        // Format season days
        const season1Days = formatSeasonDays(season1Overlap);
        const season2Days = formatSeasonDays(season2Overlap);

        // If we have GPO data, use it
        performanceRow = {
  
          index: gpoData.index,
          grossProfit: gpoData.grossProfit,
          maxBid: gpoData.maxBid,
          startDate: gpoData.startDate,
          days: gpoData.days,
          profitPerDay: gpoData.profitPerDay,
          endDate: gpoData.endDate,
          season1Days: season1Days,
          season2Days: season2Days,
          farmingStartDate,
          farmingEndDate,
          daysFarming: null // Will be calculated later
        };
      } else {
        // If we don't have GPO data, create a row with null/empty values for GPO fields
        // but keep the farming dates
        performanceRow = {
          index: 0, // This will be hidden in the UI
          grossProfit: 0, // This will be hidden in the UI
          maxBid: 0, // This will be hidden in the UI
          startDate: new Date(0), // This will be hidden in the UI
          days: 0, // This will be hidden in the UI
          profitPerDay: 0, // This will be hidden in the UI
          endDate: new Date(0), // This will be hidden in the UI
          season1Days: null,
          season2Days: null,
          farmingStartDate,
          farmingEndDate,
          daysFarming: null // Will be calculated later
        };
      }

      performanceRows.push(performanceRow);
    }
    
    // Collect all farming dates for "Days Farming" calculation
    const allFarmingDates = farmingDateEntries.filter(entry => entry.start !== null);
    
    // Calculate days farming for each row
    for (let i = 0; i < performanceRows.length; i++) {
      const row = performanceRows[i];
      
      // Only calculate days farming for rows with valid GPO data (grossProfit > 0)
      if (row.grossProfit > 0) {
        try {
          // Pass all farming dates to calculate the total days farming
          row.daysFarming = await calculateDaysFarming(
            row.startDate, 
            row.endDate, 
            allFarmingDates
          );
        } catch (error) {
          // Continue with null value
        }
      }
    }
    
    return performanceRows;
  } catch (error) {
    throw error;
  }
};